import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import api from '../services/api'
import { debounce } from 'lodash'

const PreferencesContext = createContext()

export const usePreferences = () => {
    const context = useContext(PreferencesContext)
    if (!context) {
        throw new Error('usePreferences must be used within a PreferencesProvider')
    }
    return context
}

// Default preferences - cleaned up
const defaultPreferences = {
    theme: 'light', // 'light', 'dark'
    highContrast: false,
    cameraLayout: {
        cameraOrder: [] // Array of camera IDs in custom order
    },
    volumeSettings: {
        globalMuted: true,
        individualVolumes: {} // { cameraId: volume }
    },
    favorites: [], // Array of favorite camera IDs
    recentCameras: [], // Array of recently viewed camera IDs
    filters: {
        showOffline: true,
        groupByLocation: false,
        selectedLocations: [],
        selectedTypes: []
    },
    alerts: {
        motion: true,
        offline: true,
        sound: false,
        email: false
    },
    accessibility: {
        reduceMotion: false,
        focusIndicators: true
    },
    screens: {
        activeScreenId: null,
        lastUsedScreens: [],
        autoRotation: {
            enabled: false,
            interval: 30,
            screenOrder: [],
            screenDurations: {},
            currentScreenIndex: 0,
            pauseOnActivity: true,
            adaptiveTiming: true
        },
        screenConfigs: {}
    }
}


// Function to clean up obsolete preferences data
const cleanupObsoleteData = (prefs) => {
    const cleaned = { ...prefs }
    
    // Remove obsolete cameraLayout fields
    if (cleaned.cameraLayout) {
        delete cleaned.cameraLayout.gridSize
        delete cleaned.cameraLayout.savedLayouts
        delete cleaned.cameraLayout.activeLayout
    }
    
    // Remove obsolete accessibility fields
    if (cleaned.accessibility) {
        delete cleaned.accessibility.keyboardShortcuts
    }
    
    return cleaned
}

export const PreferencesProvider = ({ children }) => {
    const { user } = useAuth()
    const [preferences, setPreferences] = useState(() => {
        // Load preferences from localStorage as initial fallback
        const saved = localStorage.getItem('cameraPreferences')
        const rawPrefs = saved ? JSON.parse(saved) : defaultPreferences
        
        // Clean up obsolete data and merge with defaults
        const cleanedPrefs = cleanupObsoleteData(rawPrefs)
        const mergedPrefs = { ...defaultPreferences, ...cleanedPrefs }
        
        // Save cleaned preferences back to localStorage
        localStorage.setItem('cameraPreferences', JSON.stringify(mergedPrefs))
        
        return mergedPrefs
    })
    const [isSyncing, setIsSyncing] = useState(false)

    // Load preferences from API when user is authenticated
    useEffect(() => {
        if (user && user.preferences) {
            console.log('Loading preferences from API:', user.preferences)
            // Clean up obsolete data from API
            const cleanedApiPrefs = cleanupObsoleteData(user.preferences)
            // Merge API preferences with defaults to ensure all fields exist
            const mergedPreferences = { ...defaultPreferences, ...cleanedApiPrefs }
            setPreferences(mergedPreferences)
            // Also update localStorage for offline access
            localStorage.setItem('cameraPreferences', JSON.stringify(mergedPreferences))
        } else if (user === null) {
            // User is logged out, reset to defaults and clear localStorage
            console.log('User logged out, resetting preferences to defaults')
            setPreferences(defaultPreferences)
            localStorage.removeItem('cameraPreferences')
        }
    }, [user])

    // Debounced API sync function
    const syncPreferencesToAPI = useCallback(
        debounce(async (newPreferences) => {
            if (!user) return

            try {
                setIsSyncing(true)
                console.log('PreferencesContext - Syncing preferences to API:', newPreferences)
                console.log('PreferencesContext - Favorites being sent:', newPreferences.favorites)
                const response = await api.put('/auth/preferences', { preferences: newPreferences })
                console.log('PreferencesContext - API response:', response.data)
                console.log('PreferencesContext - Preferences synced successfully')
            } catch (error) {
                console.error('PreferencesContext - Failed to sync preferences to API:', error)
                console.error('PreferencesContext - Error details:', error.response?.data)
                // Continue to work with local preferences even if sync fails
            } finally {
                setIsSyncing(false)
            }
        }, 1000), // Debounce for 1 second to avoid excessive API calls
        [user]
    )

    // Save preferences to both localStorage and API
    useEffect(() => {
        localStorage.setItem('cameraPreferences', JSON.stringify(preferences))
        
        // Sync to API if user is authenticated
        if (user) {
            syncPreferencesToAPI(preferences)
        }
    }, [preferences, user, syncPreferencesToAPI])

    // Theme management
    useEffect(() => {
        const root = document.documentElement
        if (preferences.theme === 'dark') {
            root.classList.add('dark')
        } else {
            root.classList.remove('dark')
        }

        if (preferences.highContrast) {
            root.classList.add('high-contrast')
        } else {
            root.classList.remove('high-contrast')
        }
    }, [preferences.theme, preferences.highContrast])

    const updatePreference = (key, value) => {
        setPreferences(prev => ({
            ...prev,
            [key]: value
        }))
    }

    const updateNestedPreference = (category, key, value) => {
        setPreferences(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }))
    }


    // Favorites and recent cameras
    const toggleFavorite = (cameraId) => {
        const isFavorite = preferences.favorites.includes(cameraId)
        const newFavorites = isFavorite
            ? preferences.favorites.filter(id => id !== cameraId)
            : [...preferences.favorites, cameraId]
        
        setPreferences(prev => ({
            ...prev,
            favorites: newFavorites
        }))
    }

    const addRecentCamera = (cameraId) => {
        const recent = preferences.recentCameras.filter(id => id !== cameraId)
        updatePreference('recentCameras', [cameraId, ...recent].slice(0, 10)) // Keep last 10
    }

    // Volume settings
    const setCameraVolume = (cameraId, volume) => {
        updateNestedPreference('volumeSettings', 'individualVolumes', {
            ...preferences.volumeSettings.individualVolumes,
            [cameraId]: volume
        })
    }

    const toggleGlobalMute = () => {
        updateNestedPreference('volumeSettings', 'globalMuted', !preferences.volumeSettings.globalMuted)
    }

    // Theme and accessibility
    const toggleTheme = () => {
        updatePreference('theme', preferences.theme === 'light' ? 'dark' : 'light')
    }

    const toggleHighContrast = () => {
        updatePreference('highContrast', !preferences.highContrast)
    }

    // Screen management functions
    const generateScreenId = () => {
        return `screen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }

    const createScreen = (screenConfig) => {
        const screenId = generateScreenId()
        const newScreen = {
            id: screenId,
            name: screenConfig.name || `Screen ${Object.keys(preferences.screens?.screenConfigs || {}).length + 1}`,
            thumbnail: screenConfig.thumbnail || null,
            template: screenConfig.template || 'custom',
            usageCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            grid: {
                type: screenConfig.grid?.type || 'symmetric',
                columns: screenConfig.grid?.columns || 2,
                rows: screenConfig.grid?.rows || 2,
                cellGaps: screenConfig.grid?.cellGaps || 2,
                aspectRatio: screenConfig.grid?.aspectRatio || '16:9',
                backgroundColor: screenConfig.grid?.backgroundColor || '#1a1a1a'
            },
            cameras: screenConfig.cameras || [],
            displaySettings: {
                showCameraNames: screenConfig.displaySettings?.showCameraNames !== false,
                showTimestamps: screenConfig.displaySettings?.showTimestamps !== false,
                autoHideControls: screenConfig.displaySettings?.autoHideControls || 3000
            }
        }

        setPreferences(prev => ({
            ...prev,
            screens: {
                ...prev.screens,
                screenConfigs: {
                    ...prev.screens.screenConfigs,
                    [screenId]: newScreen
                },
                activeScreenId: prev.screens.activeScreenId || screenId,
                lastUsedScreens: [screenId, ...prev.screens.lastUsedScreens.filter(id => id !== screenId)].slice(0, 5),
                autoRotation: {
                    ...prev.screens.autoRotation,
                    // Auto-add new screen to rotation order
                    screenOrder: [...prev.screens.autoRotation.screenOrder, screenId]
                }
            }
        }))

        return screenId
    }

    const updateScreen = (screenId, updates) => {
        if (!preferences?.screens?.screenConfigs?.[screenId]) return false

        setPreferences(prev => ({
            ...prev,
            screens: {
                ...prev.screens,
                screenConfigs: {
                    ...prev.screens.screenConfigs,
                    [screenId]: {
                        ...prev.screens.screenConfigs[screenId],
                        ...updates,
                        updatedAt: new Date().toISOString()
                    }
                }
            }
        }))

        return true
    }

    const deleteScreen = (screenId) => {
        if (!preferences?.screens?.screenConfigs?.[screenId]) return false

        const { [screenId]: deleted, ...remainingScreens } = preferences.screens.screenConfigs || {}
        
        setPreferences(prev => ({
            ...prev,
            screens: {
                ...prev.screens,
                screenConfigs: remainingScreens,
                activeScreenId: prev.screens.activeScreenId === screenId 
                    ? Object.keys(remainingScreens)[0] || null 
                    : prev.screens.activeScreenId,
                lastUsedScreens: prev.screens.lastUsedScreens.filter(id => id !== screenId),
                autoRotation: {
                    ...prev.screens.autoRotation,
                    screenOrder: prev.screens.autoRotation.screenOrder.filter(id => id !== screenId)
                }
            }
        }))

        return true
    }

    const setActiveScreen = (screenId) => {
        if (!preferences?.screens?.screenConfigs?.[screenId]) return false

        // Increment usage count
        updateScreen(screenId, { 
            usageCount: (preferences.screens.screenConfigs?.[screenId]?.usageCount || 0) + 1 
        })

        setPreferences(prev => ({
            ...prev,
            screens: {
                ...prev.screens,
                activeScreenId: screenId,
                lastUsedScreens: [screenId, ...prev.screens.lastUsedScreens.filter(id => id !== screenId)].slice(0, 5)
            }
        }))

        return true
    }

    const updateAutoRotation = (settings) => {
        setPreferences(prev => ({
            ...prev,
            screens: {
                ...prev.screens,
                autoRotation: {
                    ...prev.screens.autoRotation,
                    ...settings
                }
            }
        }))
    }

    const reorderScreensForRotation = (screenOrder) => {
        updateAutoRotation({ screenOrder })
    }

    const getActiveScreen = () => {
        if (!preferences?.screens?.activeScreenId) return null
        return preferences.screens.screenConfigs?.[preferences.screens.activeScreenId] || null
    }

    const getScreens = () => {
        if (!preferences?.screens?.screenConfigs) {
            return []
        }
        return Object.values(preferences.screens.screenConfigs).sort((a, b) => 
            new Date(b.updatedAt) - new Date(a.updatedAt)
        )
    }

    const getMostUsedScreens = (limit = 5) => {
        if (!preferences?.screens?.screenConfigs) {
            return []
        }
        return Object.values(preferences.screens.screenConfigs)
            .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
            .slice(0, limit)
    }

    const setScreenDuration = (screenId, duration) => {
        const currentDurations = preferences.screens.autoRotation.screenDurations || {}
        updateAutoRotation({
            screenDurations: {
                ...currentDurations,
                [screenId]: duration
            }
        })
    }

    const getScreenDuration = (screenId) => {
        const screenDurations = preferences.screens.autoRotation.screenDurations || {}
        return screenDurations[screenId] || preferences.screens.autoRotation.interval
    }

    const getRotationScreens = () => {
        if (!preferences?.screens?.screenConfigs) {
            return []
        }
        const screenOrder = preferences.screens.autoRotation.screenOrder
        if (screenOrder.length === 0) {
            // When no explicit order, return screens sorted by name for predictable rotation
            return Object.values(preferences.screens.screenConfigs)
                .sort((a, b) => a.name.localeCompare(b.name))
        }
        return screenOrder.map(id => preferences.screens.screenConfigs[id]).filter(Boolean)
    }

    const goToNextScreen = () => {
        const rotationScreens = getRotationScreens()
        if (rotationScreens.length === 0) return null

        const currentIndex = preferences.screens.autoRotation.currentScreenIndex || 0
        // Validate current index is within bounds
        const validCurrentIndex = currentIndex >= rotationScreens.length ? 0 : currentIndex
        const nextIndex = (validCurrentIndex + 1) % rotationScreens.length
        const nextScreen = rotationScreens[nextIndex]

        if (!nextScreen) {
            console.warn('No next screen found, resetting to first screen')
            const firstScreen = rotationScreens[0]
            updateAutoRotation({ currentScreenIndex: 0 })
            setActiveScreen(firstScreen.id)
            return firstScreen
        }

        console.log(`AutoRotation: Advancing from screen ${validCurrentIndex} to screen ${nextIndex} (${nextScreen.name})`)
        updateAutoRotation({ currentScreenIndex: nextIndex })
        setActiveScreen(nextScreen.id)
        return nextScreen
    }

    const goToPreviousScreen = () => {
        const rotationScreens = getRotationScreens()
        if (rotationScreens.length === 0) return null

        const currentIndex = preferences.screens.autoRotation.currentScreenIndex || 0
        // Validate current index is within bounds
        const validCurrentIndex = currentIndex >= rotationScreens.length ? 0 : currentIndex
        const prevIndex = validCurrentIndex === 0 ? rotationScreens.length - 1 : validCurrentIndex - 1
        const prevScreen = rotationScreens[prevIndex]

        if (!prevScreen) {
            console.warn('No previous screen found, resetting to last screen')
            const lastScreen = rotationScreens[rotationScreens.length - 1]
            updateAutoRotation({ currentScreenIndex: rotationScreens.length - 1 })
            setActiveScreen(lastScreen.id)
            return lastScreen
        }

        console.log(`AutoRotation: Going back from screen ${validCurrentIndex} to screen ${prevIndex} (${prevScreen.name})`)
        updateAutoRotation({ currentScreenIndex: prevIndex })
        setActiveScreen(prevScreen.id)
        return prevScreen
    }

    const startAutoRotation = () => {
        const rotationScreens = getRotationScreens()
        if (rotationScreens.length === 0) return

        // Set to first screen in rotation
        updateAutoRotation({ 
            enabled: true,
            currentScreenIndex: 0 
        })
        setActiveScreen(rotationScreens[0].id)
    }

    const value = {
        preferences,
        updatePreference,
        updateNestedPreference,
        isSyncing,
        
        // Favorites and recent
        toggleFavorite,
        addRecentCamera,
        
        // Volume
        setCameraVolume,
        toggleGlobalMute,
        
        // Theme
        toggleTheme,
        toggleHighContrast,

        // Screen management
        createScreen,
        updateScreen,
        deleteScreen,
        setActiveScreen,
        updateAutoRotation,
        reorderScreensForRotation,
        getActiveScreen,
        getScreens,
        getMostUsedScreens,
        setScreenDuration,
        getScreenDuration,
        getRotationScreens,
        goToNextScreen,
        goToPreviousScreen,
        startAutoRotation
    }

    return (
        <PreferencesContext.Provider value={value}>
            {children}
        </PreferencesContext.Provider>
    )
}